import React from 'react';
import { isLink } from '@/util/helpers/string-helpers';
import { OTHER_LABEL } from '@/components/ChartHolder.constants';
import { INT, FLOAT, STRING, BOOL } from '../ColumnType';
import {
  COLUMN_TYPE_OPTIONS_MAP,
  NUMBER_FORMAT_KEY,
  ROUNDING_KEY,
  KEYS_TO_OPTIONS_MAP,
} from '../../components/ChartFormattingOptions';
import { CONFIG_FORMATTING } from '../../components/charting/chartingConfiguration.constants';

export function cellFormatter(
  dataTable,
  getChartOption,
  columnIndex,
  isDashboardTable,
) {
  const heading = dataTable.headings()[columnIndex];
  const colType = heading.columnType();
  const headerName = heading.columnName().toString();
  const formatting = getChartOption(CONFIG_FORMATTING(headerName));
  return (val) =>
    formatChartCell(val, formatting, colType, isDashboardTable, heading);
}

export function cellFormat(dataTable, getChartOption, columnIndex) {
  const heading = dataTable.headings()[columnIndex];
  const headerName = heading.columnName().toString();
  const formatting = getChartOption(CONFIG_FORMATTING(headerName));
  return formatting;
}

export function formatChartCell(
  value,
  formatOptions,
  colType,
  isDashboardTable,
  heading,
) {
  if (value === OTHER_LABEL) {
    return value;
  }

  const defaultOptions = COLUMN_TYPE_OPTIONS_MAP[colType.type()];

  const formatOptionObj = formatOptions || {};

  // will hold config details, including defaults in here.
  const configObj = {};

  // add formatoptions to config obj, including defaults if no value has been set
  defaultOptions.forEach((option) => {
    const valueFromState = formatOptionObj[option.keyToUpdate];
    // const valueFromDefault = defaultOptionObj[option.keyToUpdate];
    if (valueFromState) {
      configObj[option.keyToUpdate] = valueFromState;
    } else if (option.setDefault) {
      configObj[option.keyToUpdate] = option.defaultOption(heading);
    }
  });

  // get the keys from the format object.
  let formatKeys = Object.keys(configObj);

  // value to be formatted
  let newValue = value;

  // return NULL for null values, without applying any formatting.
  if (colType.nullable() && (!newValue || newValue === 'null')) {
    return isDashboardTable ? <span className="faint">NULL</span> : 'NULL';
  }

  // return "EMPTY" for empty strings, without applying any formatting.
  if (
    newValue &&
    colType.type() === STRING &&
    String(newValue).trim().length === 0
  ) {
    return isDashboardTable ? <span className="faint">EMPTY</span> : 'EMPTY';
  }

  // the following items need to be appear in the formatKeys array in a specific order (with rounding_key ALWAYS first).
  const goFirst = [NUMBER_FORMAT_KEY, ROUNDING_KEY];

  // Filter out the items above whose formatting needs to be applied first (these will be added below).
  formatKeys = formatKeys.filter((key) => {
    return !goFirst.includes(key);
  });

  // add in go first items at the start of the array.
  goFirst.forEach((item) => {
    if (configObj[item]) {
      formatKeys.unshift(item);
    }
  });

  // convert "t" to true
  if (colType.type() === BOOL) {
    newValue = formatBOOLS(newValue);
  }

  // loop through each key and update the value with the new formatting.
  formatKeys.forEach((formatKey) => {
    // the selected (or default) formatting for the key
    const formatValue = configObj[formatKey];

    // get formatting function. For options from select menus, this is inside an array of options
    const format = Array.isArray(KEYS_TO_OPTIONS_MAP[formatKey])
      ? KEYS_TO_OPTIONS_MAP[formatKey].find((item) => item.id === formatValue)
          .formatting
      : KEYS_TO_OPTIONS_MAP[formatKey].formatting;

    const formatProps = {
      formatKeyValue: formatValue,
      colType,
      roundingKey: configObj[ROUNDING_KEY],
      nonFormattedValue: value,
    };

    newValue = format(newValue, formatProps);
  });

  // add hyper links
  if (
    newValue &&
    colType.type() === STRING &&
    isDashboardTable &&
    isLink(newValue)
  ) {
    return (
      <a href={newValue} target="_blank" rel="noopener">
        {newValue}
      </a>
    );
  }

  // make values that are just 0s faint
  if (
    newValue &&
    (colType.type() === INT || colType.type() === FLOAT) &&
    isDashboardTable
  ) {
    // the +"" converts INT to a string (as sometimes the value comes through as a string)
    const removeDots = `${newValue}`.replace(/[.,]/g, '');
    const onlyZeros = /^0*$/.test(removeDots);
    if (onlyZeros) {
      return <span className="faint">{newValue}</span>;
    }
  }

  return newValue;
}

function formatBOOLS(newValue) {
  return newValue === 't' || newValue === '1'
    ? 'true'
    : newValue === 'f' || newValue === '0'
    ? 'false'
    : newValue;
}
